"use strict";
/**
 * Native Firestore Adapter
 * Provides a unified interface for Firestore operations that works on both web and native platforms
 * - Uses @capacitor-firebase/firestore on iOS/Android for native performance
 * - Uses firebase/firestore web SDK on web browsers
 */
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.nativeFirestoreAdapter = exports.isNativePlatform = void 0;
const core_1 = require("@capacitor/core");
/**
 * Check if running on native platform (iOS/Android)
 */
const isNativePlatform = () => {
    return core_1.Capacitor.isNativePlatform();
};
exports.isNativePlatform = isNativePlatform;
/**
 * Native Firestore Adapter
 * Routes Firestore operations to the appropriate SDK based on platform
 */
class NativeFirestoreAdapter {
    /**
     * Get documents from a collection with optional constraints
     * On native, orderBy is applied in memory to avoid composite index requirements
     */
    getCollection(collectionPath, whereConstraints = [], orderByConstraints = [], limitCount, startAfterDocId) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!(0, exports.isNativePlatform)()) {
                throw new Error('Native Firestore not available - use web SDK instead');
            }
            try {
                // Dynamic import of the Capacitor Firestore plugin
                const { FirebaseFirestore } = yield Promise.resolve().then(() => __importStar(require('@capacitor-firebase/firestore')));
                // IMPORTANT: We always include orderBy in the native query because:
                // The Capacitor Firebase plugin silently drops Firestore Timestamp fields from
                // serialized data (fields set via serverTimestamp() don't appear in snapshot.data).
                // This makes in-memory sorting impossible for timestamp-based ordering.
                // Instead, we let Firestore sort server-side where it HAS access to timestamps.
                // The 15-second timeout below protects against missing composite indexes.
                const hasOrderBy = orderByConstraints.length > 0;
                const hasWhere = whereConstraints.length > 0;
                if (hasOrderBy && hasWhere) {
                    console.log(`📱 Native Firestore: Server-side orderBy for ${collectionPath} (timeout protects against missing indexes)`);
                }
                // Build query options for Capacitor Firestore plugin
                const queryOptions = {
                    reference: collectionPath
                };
                // Handle where constraints using compositeFilter
                if (whereConstraints.length > 0) {
                    const whereFilters = whereConstraints.map(constraint => ({
                        type: 'where',
                        fieldPath: constraint.fieldPath,
                        opStr: constraint.opStr,
                        value: constraint.value
                    }));
                    queryOptions.compositeFilter = {
                        type: 'and',
                        queryConstraints: whereFilters
                    };
                }
                // Build non-filter constraints - always include orderBy and limit
                const nonFilterConstraints = [];
                if (orderByConstraints.length > 0) {
                    nonFilterConstraints.push(...orderByConstraints.map(orderBy => ({
                        type: 'orderBy',
                        fieldPath: orderBy.fieldPath,
                        directionStr: orderBy.direction
                    })));
                }
                if (startAfterDocId !== undefined) {
                    nonFilterConstraints.push({
                        type: 'startAfter',
                        reference: `${collectionPath}/${startAfterDocId}`
                    });
                }
                if (limitCount !== undefined) {
                    nonFilterConstraints.push({
                        type: 'limit',
                        limit: limitCount
                    });
                }
                queryOptions.queryConstraints = nonFilterConstraints;
                // Add timeout to prevent hanging (protects against missing composite indexes)
                const QUERY_TIMEOUT = 15000; // 15 seconds
                const timeoutPromise = new Promise((_, reject) => {
                    setTimeout(() => reject(new Error(`Native Firestore getCollection timed out after ${QUERY_TIMEOUT}ms for ${collectionPath}`)), QUERY_TIMEOUT);
                });
                const result = yield Promise.race([
                    FirebaseFirestore.getCollection(queryOptions),
                    timeoutPromise
                ]);
                // Convert to unified format
                // Note: Results are already sorted by Firestore server-side
                const documents = (result.snapshots || []).map((snapshot) => ({
                    id: snapshot.id,
                    data: snapshot.data
                }));
                console.log(`✅ Native Firestore: Got ${documents.length} docs from ${collectionPath}${hasOrderBy ? ' (server-sorted)' : ''}`);
                return {
                    documents,
                    size: documents.length
                };
            }
            catch (error) {
                console.error(`❌ Native Firestore: getCollection failed for ${collectionPath}:`, error);
                throw error;
            }
        });
    }
    /**
     * Get a nested value from an object using dot notation
     */
    getNestedValue(obj, path) {
        if (!obj)
            return undefined;
        const parts = path.split('.');
        let current = obj;
        for (const part of parts) {
            if (current === undefined || current === null)
                return undefined;
            current = current[part];
        }
        return current;
    }
    /**
     * Compare two values for sorting (handles Firestore timestamps, numbers, strings)
     */
    compareValues(a, b) {
        // Handle null/undefined
        if (a == null && b == null)
            return 0;
        if (a == null)
            return -1;
        if (b == null)
            return 1;
        // Try to convert both values to comparable timestamps
        const aTime = this.toTimestampMs(a);
        const bTime = this.toTimestampMs(b);
        if (aTime !== null && bTime !== null) {
            return aTime - bTime;
        }
        // Handle numbers
        if (typeof a === 'number' && typeof b === 'number') {
            return a - b;
        }
        // Handle strings
        if (typeof a === 'string' && typeof b === 'string') {
            return a.localeCompare(b);
        }
        // Fallback
        return String(a).localeCompare(String(b));
    }
    /**
     * Try to convert a value to timestamp milliseconds
     * Returns null if the value is not a recognizable timestamp format
     */
    toTimestampMs(val) {
        var _a;
        if (val == null)
            return null;
        // Firestore native format: { _seconds, _nanoseconds } or { seconds, nanoseconds }
        const seconds = (_a = val === null || val === void 0 ? void 0 : val._seconds) !== null && _a !== void 0 ? _a : val === null || val === void 0 ? void 0 : val.seconds;
        if (typeof seconds === 'number') {
            return seconds * 1000;
        }
        // Web SDK Timestamp with toMillis()
        if (typeof (val === null || val === void 0 ? void 0 : val.toMillis) === 'function') {
            return val.toMillis();
        }
        // Web SDK Timestamp with toDate()
        if (typeof (val === null || val === void 0 ? void 0 : val.toDate) === 'function') {
            return val.toDate().getTime();
        }
        // ISO date string (e.g., "2025-10-08T15:50:39.249Z")
        if (typeof val === 'string') {
            // Check for Timestamp string format: "Timestamp(seconds=..., nanoseconds=...)"
            const match = val.match(/Timestamp\(seconds=(\d+),\s*nanoseconds=\d+\)/);
            if (match) {
                return parseInt(match[1], 10) * 1000;
            }
            // Try ISO date string
            const date = new Date(val);
            if (!isNaN(date.getTime())) {
                return date.getTime();
            }
        }
        // Number (already in ms or seconds)
        if (typeof val === 'number') {
            // If it looks like seconds (before year 2100 in seconds), convert to ms
            if (val < 10000000000) {
                return val * 1000;
            }
            return val;
        }
        return null;
    }
    /**
     * Get a single document by path
     */
    getDocument(documentPath) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!(0, exports.isNativePlatform)()) {
                throw new Error('Native Firestore not available - use web SDK instead');
            }
            try {
                const { FirebaseFirestore } = yield Promise.resolve().then(() => __importStar(require('@capacitor-firebase/firestore')));
                // Add timeout to prevent hanging
                const DOC_TIMEOUT = 10000; // 10 seconds
                const timeoutPromise = new Promise((_, reject) => {
                    setTimeout(() => reject(new Error(`Native Firestore getDocument timed out after ${DOC_TIMEOUT}ms for ${documentPath}`)), DOC_TIMEOUT);
                });
                const result = yield Promise.race([
                    FirebaseFirestore.getDocument({ reference: documentPath }),
                    timeoutPromise
                ]);
                if (!result.snapshot || !result.snapshot.id || !result.snapshot.data) {
                    return null;
                }
                return {
                    id: result.snapshot.id,
                    data: result.snapshot.data
                };
            }
            catch (error) {
                console.error(`❌ Native Firestore: getDocument failed for ${documentPath}:`, error);
                throw error;
            }
        });
    }
    /**
     * Set a document (create or update)
     */
    setDocument(documentPath, data, merge = false) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!(0, exports.isNativePlatform)()) {
                throw new Error('Native Firestore not available - use web SDK instead');
            }
            try {
                const { FirebaseFirestore } = yield Promise.resolve().then(() => __importStar(require('@capacitor-firebase/firestore')));
                console.log('📝 Native Firestore: Setting document:', documentPath);
                yield FirebaseFirestore.setDocument({
                    reference: documentPath,
                    data,
                    merge
                });
                console.log('✅ Native Firestore: Document set');
            }
            catch (error) {
                console.error('❌ Native Firestore: setDocument failed:', error);
                throw error;
            }
        });
    }
    /**
     * Delete a document
     */
    deleteDocument(documentPath) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!(0, exports.isNativePlatform)()) {
                throw new Error('Native Firestore not available - use web SDK instead');
            }
            try {
                const { FirebaseFirestore } = yield Promise.resolve().then(() => __importStar(require('@capacitor-firebase/firestore')));
                console.log('🗑️ Native Firestore: Deleting document:', documentPath);
                yield FirebaseFirestore.deleteDocument({
                    reference: documentPath
                });
                console.log('✅ Native Firestore: Document deleted');
            }
            catch (error) {
                console.error('❌ Native Firestore: deleteDocument failed:', error);
                throw error;
            }
        });
    }
    /**
     * Update a document
     */
    updateDocument(documentPath, data) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!(0, exports.isNativePlatform)()) {
                throw new Error('Native Firestore not available - use web SDK instead');
            }
            try {
                const { FirebaseFirestore } = yield Promise.resolve().then(() => __importStar(require('@capacitor-firebase/firestore')));
                console.log('📝 Native Firestore: Updating document:', documentPath);
                yield FirebaseFirestore.updateDocument({
                    reference: documentPath,
                    data
                });
                console.log('✅ Native Firestore: Document updated');
            }
            catch (error) {
                console.error('❌ Native Firestore: updateDocument failed:', error);
                throw error;
            }
        });
    }
    /**
     * Add a new document with auto-generated ID
     */
    addDocument(collectionPath, data) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!(0, exports.isNativePlatform)()) {
                throw new Error('Native Firestore not available - use web SDK instead');
            }
            try {
                const { FirebaseFirestore } = yield Promise.resolve().then(() => __importStar(require('@capacitor-firebase/firestore')));
                console.log('➕ Native Firestore: Adding document to:', collectionPath);
                const result = yield FirebaseFirestore.addDocument({
                    reference: collectionPath,
                    data
                });
                const docId = result.reference.id;
                console.log('✅ Native Firestore: Document added with ID:', docId);
                return docId;
            }
            catch (error) {
                console.error('❌ Native Firestore: addDocument failed:', error);
                throw error;
            }
        });
    }
}
// Export singleton instance
exports.nativeFirestoreAdapter = new NativeFirestoreAdapter();
